import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Response } from 'express';
import { Observable } from 'rxjs';
import { isLoginPath, RESPONSE } from 'utils';
import { environment } from '../../environments/environment';

/**
 * When migrating from legacy to Kitsune only allow some routes
 * to be used on Kitsune.
 *
 * This guard checks a list of whitelisted URLs and if the requested
 * path does not match (i.e. is not permitted/handled by Kitsune at this time)
 * then forces a hard refresh of the page.
 *
 * This triggers a new request to Cloudfront which then correctly routes the
 * request to the legacy application (yes, the whitelist here has to be kept
 * in sync with a whitelist on Cloudfront...).
 */
@Injectable({
  providedIn: 'root',
})
export class ExternalUrlsGuard implements CanActivate {
  private document = inject<Document>(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private response = inject<Response>(RESPONSE, { optional: true });

  public get window(): Window {
    return this.document.defaultView;
  }

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (isLoginPath(route.url)) {
      return true;
    }
    for (const r of environment.internalUrls) {
      const path =
        '/' +
        route.pathFromRoot
          .map(snapshot => snapshot.url.map(segment => segment.path))
          .flat()
          .join('/');
      if (r.test(path)) {
        return true;
      }
    }
    // Cloudfront should protect the server from receiving requests to legacy
    // but if a user is in Kitsune and navigates to a route that is only on legacy
    // this should trigger a page reload
    if (isPlatformBrowser(this.platformId)) {
      this.window.open(`${environment.legacyRedirect}${decodeURIComponent(state.url)}`, '_self');
    } else {
      // On the server we are going to return a 416 error which will cause cloudfront to fall back to legacy
      this.response?.status(416);
    }
    return false;
  }
}
